/** Folder-sync file API for a session workspace.
 *
 * All endpoints are scoped by `sessionId` — the backend derives the workspace
 * directory from that and scopes everything via space ownership checks.
 */

import { apiFetch } from "@/shared/api/http-client";

export interface UploadEntry {
  path: string;
  data: Blob;
}

export interface ManifestEntry {
  path: string;
  hash: string;
  size: number;
}

export interface SyncDiffResult {
  onlyLocal: ManifestEntry[];
  onlyServer: ManifestEntry[];
  different: { path: string; localHash: string; serverHash: string; size: number }[];
  dirsOnlyLocal: string[];
  dirsOnlyServer: string[];
}

const base = (sessionId: string) => `/api/sessions/${encodeURIComponent(sessionId)}/files`;

export async function uploadFiles(sessionId: string, files: UploadEntry[], opId?: string): Promise<void> {
  if (files.length === 0) return;
  const form = new FormData();
  for (const f of files) form.append("files", f.data, f.path);
  const r = await apiFetch(base(sessionId), {
    method: "POST",
    body: form,
    headers: opId ? { "X-Op-Id": opId } : undefined,
  });
  if (!r.ok) throw new Error(`upload ${r.status}`);
}

export async function getFileContent(
  sessionId: string,
  path: string,
): Promise<{ path: string; content: string; encoding?: string }> {
  const r = await apiFetch(`${base(sessionId)}/content?path=${encodeURIComponent(path)}`);
  if (!r.ok) throw new Error(`content ${r.status}`);
  return r.json();
}

export async function deleteFiles(sessionId: string, paths: string[], opId?: string): Promise<void> {
  if (paths.length === 0) return;
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (opId) headers["X-Op-Id"] = opId;
  const r = await apiFetch(`${base(sessionId)}/delete`, {
    method: "POST",
    headers,
    body: JSON.stringify({ paths }),
  });
  if (!r.ok) throw new Error(`delete ${r.status}`);
}

export async function mkdirRemote(sessionId: string, path: string, opId?: string): Promise<void> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (opId) headers["X-Op-Id"] = opId;
  await apiFetch(`${base(sessionId)}/mkdir`, {
    method: "POST",
    headers,
    body: JSON.stringify({ path }),
  });
}

export async function rmdirRemote(sessionId: string, path: string, opId?: string): Promise<void> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (opId) headers["X-Op-Id"] = opId;
  await apiFetch(`${base(sessionId)}/rmdir`, {
    method: "POST",
    headers,
    body: JSON.stringify({ path }),
  });
}

export async function clearWorkspace(sessionId: string): Promise<void> {
  await apiFetch(`${base(sessionId)}/clear`, { method: "POST" });
}

/** Return list of server-side file paths by asking the diff endpoint with an
 * empty client manifest — cheap, reuses the existing tree walk. */
export async function listServerFiles(sessionId: string): Promise<string[]> {
  const diff = await syncDiff(sessionId, [], []);
  return diff.onlyServer.map(e => e.path);
}

export interface FileEntry {
  path: string;
  size: number;
  mtime: number;
}

export async function listFilesDetailed(sessionId: string, signal?: AbortSignal): Promise<FileEntry[]> {
  const r = await apiFetch(`${base(sessionId)}/list`, { signal });
  if (!r.ok) return [];
  const data = (await r.json()) as { files?: FileEntry[] };
  return data.files ?? [];
}

export async function syncDiff(
  sessionId: string,
  manifest: ManifestEntry[],
  dirs: string[],
): Promise<SyncDiffResult> {
  const r = await apiFetch(`${base(sessionId)}/sync/diff`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ manifest, dirs }),
  });
  if (!r.ok) throw new Error(`diff ${r.status}`);
  return r.json();
}

export interface FileWatchEvent {
  event: string;
  path: string;
  isDir?: boolean;
  hash?: string;
  seq: number;
  opId?: string;
}

export interface FileWatchSubscription {
  close: () => void;
}

/**
 * Subscribe to SSE workspace events with seq-based reconnect. If the server
 * replies with `{event: "resync"}`, it means the ring buffer rolled past our
 * cursor — the caller should trigger a full diff.
 *
 * `initialSince = -1` starts at the current tip (no replay).
 */
export function subscribeFileWatch(
  sessionId: string,
  onEvent: (ev: FileWatchEvent) => void,
  onResync: () => void,
  initialSince: number = 0,
): FileWatchSubscription {
  let lastSeq = initialSince;
  let closed = false;
  let controller: AbortController | null = null;
  let backoff = 1000;

  const connect = async () => {
    if (closed) return;
    controller = new AbortController();
    try {
      // Use apiFetch directly so auth + space headers are injected.
      const url = `${base(sessionId)}/watch?since=${lastSeq}`;
      const resp = await apiFetch(url, { signal: controller.signal });
      if (!resp.ok || !resp.body) {
        if (resp.status === 404) backoff = Math.min(backoff * 2, 30000);
        throw new Error(`watch ${resp.status}`);
      }
      backoff = 1000;

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        while (buf.includes("\n\n")) {
          const idx = buf.indexOf("\n\n");
          const raw = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          for (const line of raw.split("\n")) {
            if (!line.startsWith("data: ")) continue;
            try {
              const data = JSON.parse(line.slice(6));
              if (data.event === "resync") {
                lastSeq = 0;
                onResync();
                continue;
              }
              if (typeof data.seq === "number") lastSeq = data.seq;
              onEvent(data);
            } catch {}
          }
        }
      }
    } catch (err: unknown) {
      const name = (err as { name?: string })?.name;
      if (name === "AbortError" || closed) return;
    }
    if (!closed) setTimeout(connect, backoff);
  };

  connect();
  return { close: () => { closed = true; controller?.abort(); } };
}
